import {
  Megaphone,
  Search,
  Share2,
  Target,
  Palette,
  Bot,
  Brain,
  ShoppingCart,
  Wrench,
  PenTool,
  TrendingUp,
  BarChart3,
  Globe,
  Zap,
  Linkedin,
  Instagram,
  Facebook,
  MessageSquare,
  FileText,
  Lightbulb,
  ChartLine,
  Sparkles,
  LineChart,
} from 'lucide-react';

/* ============================================================
   1. DIGITAL MARKETING (PARENT HUB)
   ============================================================ */
export const digitalMarketingHub = {
  slug: 'digital-marketing',
  eyebrow: 'Digital Marketing',
  aiSummary: 'Blue Ribbon Technologies delivers full-service digital marketing for Canadian SMBs: SEO, paid ads, social, and brand strategy all coordinated as one system, with every dollar tied to a measurable business outcome.',
  stats: [{ value: 180, suffix: '%', label: 'Organic Traffic Growth' },{ value: 3.4, suffix: 'x', label: 'Return on Ad Spend (ROAS)' },{ value: 70, suffix: '+', label: 'Canadian SMBs Served' },{ value: 37, suffix: '%', label: 'Avg Cost Reduction' }],
  icon: Megaphone,
  accentClass: 'from-rose-600 to-pink-500',
  lightAccentClass: 'bg-rose-50',
  accentTextClass: 'text-rose-600',

  heroHeadline: 'Marketing That Moves the Needle, Not Just the Metrics',
  heroSubheadline:
    'Full-service digital marketing for Canadian startups and growing businesses. SEO, paid ads, social, and brand, all working together.',
  primaryCta: { label: 'Get a Free Marketing Audit', href: '/contact' },
  secondaryCta: { label: 'Explore Our Services', href: '#services-grid' },

  problemBar: [
    "You're spending on ads but can't tell what's actually working",
    'Your website gets traffic but none of it converts',
    "Your competitors are outranking you and you don't know why",
  ],

  whatWeDoHeadline: 'A marketing engine, not a list of tactics',
  whatWeDo: [
    "Most agencies sell you services. We build you a system. Every dollar of marketing spend should be tied to a measurable business outcome, leads, customers, or revenue. That's our north star.",
    'Whether you need to rank higher on Google, dominate AI search, fill the top of your funnel with paid ads, or build a brand Canadian buyers trust, we connect the dots so each channel makes the others stronger.',
  ],

  servicesBreakdownTitle: 'Our Marketing Services',
  servicesBreakdownSub:
    'Pick one service, or build a full-funnel engine across all four.',
  servicesBreakdown: [
    {
      icon: Search,
      title: 'SEO & Content Marketing',
      description:
        'Rank higher on Google and AI search engines with content that ranks and converts.',
      href: '/services/digital-marketing/seo',
    },
    {
      icon: Share2,
      title: 'Social Media Management',
      description:
        'Done-for-you content, posting, and community management across LinkedIn, Instagram, and more.',
      href: '/services/digital-marketing/social-media',
    },
    {
      icon: Target,
      title: 'PPC & Paid Advertising',
      description:
        'Google, Meta, and LinkedIn ad campaigns optimized for qualified leads, not vanity clicks.',
      href: '/services/digital-marketing/ppc',
    },
    {
      icon: Palette,
      title: 'Brand Strategy & Analytics',
      description:
        'Brand positioning, visual identity, and unified analytics dashboards to prove what works.',
      href: '/services/digital-marketing/brand-strategy',
    },
  ],

  whyBRT: [
    {
      title: 'AI + Human Strategy',
      description:
        'AI-powered tools combined with human strategy, not one or the other. We use the best of both.',
    },
    {
      title: 'Built for Canadian Businesses',
      description:
        'We understand local search, bilingual markets, PIPEDA, and the way Canadian buyers actually behave.',
    },
    {
      title: 'Tied to Revenue',
      description:
        'Every campaign tied to revenue, not vanity metrics. If it doesn’t move pipeline, we don’t do it.',
    },
  ],

  proof: {
    stats: [
      { value: 180, suffix: '%', label: 'Avg Organic Traffic Growth' },
      { value: 4.2, suffix: 'x', label: 'Average Return on Ad Spend' },
    ],
    caseStudy: {
      quote:
        'Blue Ribbon rebuilt our entire marketing engine. Within 6 months, we tripled qualified leads while cutting cost-per-lead by 38%.',
      client: 'Director of Growth, Toronto SaaS Company',
      industry: 'B2B SaaS, 25 employees',
    },
  },

  faqs: [
    {
      question: 'Do I have to commit to all services or can I pick one?',
      answer:
        'You can absolutely start with one service, most clients begin with SEO or paid ads. We design every engagement so you can layer in additional channels as you see results.',
    },
    {
      question: 'What size of business do you usually work with?',
      answer:
        'Our sweet spot is Canadian SMBs with $1M–$50M in annual revenue. We also work with funded startups looking for their first marketing team.',
    },
    {
      question: 'How is pricing structured?',
      answer:
        'Most engagements are flat monthly retainers based on scope. We don’t take a percentage of ad spend, that creates the wrong incentives. You’ll always see exactly where your dollars go.',
    },
    {
      question: 'How long until I see results?',
      answer:
        'Paid ads can show traction in 2–4 weeks. SEO typically takes 3–6 months for meaningful organic gains. Brand work shows up in conversion rates and sales cycles within 60–90 days.',
    },
  ],

  bottomCta: {
    headline: "Stop guessing. Start growing. Let's build your marketing engine.",
    label: 'Book a Free Strategy Call',
    href: '/contact',
  },
};

/* ============================================================
   2. SEO & CONTENT MARKETING (HUB)
   ============================================================ */
export const seoHub = {
  slug: 'seo',
  eyebrow: 'SEO & Content Marketing',
  aiSummary: "Blue Ribbon's SEO service combines AI-powered research with human strategy to help Canadian businesses rank on Google and get cited by AI engines like ChatGPT, Perplexity, and Gemini.",
  stats: [{ value: 140, suffix: '%', label: 'Year-1 Traffic Growth' },{ value: 8, suffix: '', label: 'Avg #1 Rankings per Client' },{ value: '2–3', suffix: 'x', label: 'AI Citation Increase' },{ value: 4, suffix: 'mo', label: 'Avg Time to First-Page Rank' }],
  icon: Search,
  accentClass: 'from-indigo-600 to-blue-500',
  lightAccentClass: 'bg-indigo-50',
  accentTextClass: 'text-indigo-600',

  heroHeadline: 'Rank Higher. Get Found. Win More Customers, Organically.',
  heroSubheadline:
    'AI-powered SEO and content strategies that drive real traffic for Canadian businesses, not just impressions.',
  primaryCta: { label: 'Get a Free SEO Audit', href: '/contact' },
  secondaryCta: { label: 'See SEO Services', href: '#services-grid' },

  problemBar: [
    "You're on page 3 of Google and getting zero calls from it",
    'You publish blogs but they never rank or convert',
    "Your competitors are dominating local search and you don't know how",
  ],

  whatWeDoHeadline: 'SEO that actually moves rankings',
  whatWeDo: [
    "SEO has changed. Google's AI updates, ChatGPT, and Perplexity have rewritten the playbook. Old tactics, keyword stuffing, link spam, generic blog posts, don't work anymore.",
    'We combine AI-powered research with human strategy to build content that ranks on Google, gets cited by AI engines, and actually converts visitors into customers.',
  ],

  servicesBreakdownTitle: 'SEO Services',
  servicesBreakdownSub: 'Five focused services covering every angle of modern search.',
  servicesBreakdown: [
    {
      icon: Bot,
      title: 'AI SEO / GEO',
      description:
        'Optimize for Google AI Overviews and Generative Engine Optimization.',
      href: '/services/digital-marketing/seo/ai-seo',
    },
    {
      icon: Brain,
      title: 'LLM Optimization',
      description:
        'Get cited by ChatGPT, Claude, Gemini, and Perplexity when buyers ask.',
      href: '/services/digital-marketing/seo/llm-optimization',
    },
    {
      icon: ShoppingCart,
      title: 'Ecommerce SEO',
      description:
        'Product, category, and marketplace SEO for Shopify, WooCommerce, and more.',
      href: '/services/digital-marketing/seo/ecommerce',
    },
    {
      icon: Wrench,
      title: 'Technical SEO',
      description:
        'Core Web Vitals, schema, crawl budgets, and site architecture done right.',
      href: '/services/digital-marketing/seo/technical',
    },
    {
      icon: PenTool,
      title: 'SEO Copywriting',
      description:
        'Bilingual content that ranks and converts, written for humans, optimized for search.',
      href: '/services/digital-marketing/seo/copywriting',
    },
  ],

  howItWorks: [
    {
      title: 'SEO Audit & Keyword Research',
      description:
        'We identify gaps, opportunities, and the keywords that will actually drive revenue.',
    },
    {
      title: 'Strategy & Content Roadmap',
      description:
        'A 6–12 month plan with topic clusters, priority pages, and clear milestones.',
    },
    {
      title: 'On-Page + Technical Execution',
      description:
        'We write, publish, and optimize. We fix technical issues. We build the foundation.',
    },
    {
      title: 'Track, Report & Optimize',
      description:
        'Live rank tracking, monthly reports, and continuous refinement based on what works.',
    },
  ],

  whyBRT: [
    {
      title: 'AI-First, Not AI-Only',
      description:
        'We use AI for research and scale, but every piece of content is reviewed by a human editor.',
    },
    {
      title: 'Bilingual Capability',
      description:
        'Native English and French SEO, we serve both major Canadian markets fluently.',
    },
    {
      title: 'Transparent Reporting',
      description:
        'Live dashboards, weekly check-ins, monthly strategy calls. You always know what’s working.',
    },
  ],

  proof: {
    stats: [
      { value: 140, suffix: '%', label: 'Avg Year-1 Organic Traffic Growth' },
      { value: 8, suffix: '', label: 'Avg #1 Rankings per Client' },
    ],
    caseStudy: {
      quote:
        'We went from page 4 to ranking #1 for our three most valuable keywords in eight months. Inbound leads tripled.',
      client: 'CMO, Vancouver Manufacturing Company',
      industry: 'B2B Manufacturing',
    },
  },

  faqs: [
    {
      question: 'How long does SEO take to show results?',
      answer:
        'Quick wins (technical fixes, low-competition keywords) typically show in 4–8 weeks. Significant traffic and ranking gains for competitive terms usually take 4–6 months. We focus on high-velocity wins early to build momentum.',
    },
    {
      question: 'Do you work with businesses outside major Canadian cities?',
      answer:
        'Absolutely. We serve clients from Vancouver Island to Newfoundland. Local SEO is one of our specialties, we’ve helped businesses dominate search in cities of every size.',
    },
    {
      question: 'What makes your SEO different from other agencies?',
      answer:
        'Three things: (1) we optimize for AI search engines, not just Google; (2) every deliverable is human-reviewed; (3) we tie every action to revenue, not just rankings.',
    },
    {
      question: 'Can you handle both English and French SEO?',
      answer:
        'Yes, fully bilingual. We have native French-speaking writers and SEO specialists. We handle hreflang setup, French keyword research, and culturally-localized content.',
    },
  ],

  bottomCta: {
    headline: "Your competitors are ranking. Let's make sure you are too.",
    label: 'Book a Free SEO Audit',
    href: '/contact',
  },
};

/* ============================================================
   3. AI SEO / GEO
   ============================================================ */
export const aiSeoPage = {
  slug: 'ai-seo',
  eyebrow: 'AI SEO / GEO',
  aiSummary: 'AI SEO / GEO optimizes your business to appear inside AI-generated answers on Google AI Overviews, ChatGPT, Perplexity, and Gemini, using entity building, structured data, and citation-friendly content.',
  stats: [{ value: 22, suffix: '%', label: 'AI Overview Capture Rate' },{ value: '2–3', suffix: 'x', label: 'Avg AI Citations Increase' },{ value: 60, suffix: ' days', label: 'To First AI Mention' },{ value: 30, suffix: '%', label: 'Of Searches Now AI-Answered' }],
  icon: Bot,
  accentClass: 'from-blue-600 to-cyan-500',
  lightAccentClass: 'bg-blue-50',
  accentTextClass: 'text-blue-600',

  heroHeadline: 'Be Found Everywhere Your Buyers Are Searching, Including AI.',
  heroSubheadline:
    'Traditional SEO isn’t enough anymore. We optimize your business for Google, AI search engines, and Generative Engine Optimization (GEO) so you show up wherever your customers ask questions.',
  primaryCta: { label: 'Get an AI SEO Audit', href: '/contact' },
  secondaryCta: { label: 'See How GEO Works', href: '#services-grid' },

  problemBar: [
    'Your site ranks on Google but never appears in ChatGPT or AI Overviews',
    "You don't know how to optimize for the way people search with AI",
    "Your old SEO playbook stopped working after Google's AI updates",
  ],

  whatWeDoHeadline: 'Built for where search is going',
  whatWeDo: [
    "Traditional SEO targets Google's blue links. AI SEO targets Google's AI Overviews. GEO (Generative Engine Optimization) targets ChatGPT, Perplexity, Gemini, and the dozens of other AI tools your buyers now use to research purchases.",
    "We're already building for what's next: structured content AI engines can understand, entity-based optimization that establishes you as a trusted source, and citation strategies that get your business referenced in AI-generated answers.",
  ],
  whatWeDoCallout:
    'Over 30% of Google searches now show AI-generated answers. If your business isn’t in those answers, you’re invisible to a third of your potential customers.',

  servicesBreakdownTitle: 'AI SEO Services',
  servicesBreakdown: [
    {
      icon: Sparkles,
      title: 'AI Search Optimization',
      description:
        "Structure your content so Google's SGE and AI Overviews cite your site as the answer.",
    },
    {
      icon: Bot,
      title: 'Generative Engine Optimization (GEO)',
      description:
        'Optimize for ChatGPT, Perplexity, Gemini, and other AI tools that answer buyer questions.',
    },
    {
      icon: Globe,
      title: 'Entity & Knowledge Graph Building',
      description:
        'Establish your brand as a trusted entity that AI models reference and cite confidently.',
    },
    {
      icon: FileText,
      title: 'AI Content Strategy',
      description:
        'Build topic clusters and content architecture that AI search engines reward and surface.',
    },
  ],

  howItWorks: [
    {
      title: 'AI Search Visibility Audit',
      description:
        'We test how Google AI Overviews, ChatGPT, and Perplexity describe your business today.',
    },
    {
      title: 'Entity & Content Gap Analysis',
      description:
        'Identify the topics, questions, and entities AI engines associate with competitors but not you.',
    },
    {
      title: 'Optimize & Publish AI-Ready Content',
      description:
        'Structured, semantic, citation-friendly content engineered for AI consumption.',
    },
    {
      title: 'Monitor AI Citations & Adjust',
      description:
        'Monthly tracking of AI mentions and continuous refinement of strategy.',
    },
  ],

  whyBRT: [
    {
      title: 'Early Movers in GEO',
      description:
        'We were optimizing for AI search before most agencies even knew GEO existed.',
    },
    {
      title: 'Schema & Entity Experts',
      description:
        'Deep technical expertise in structured data, knowledge graphs, and semantic markup.',
    },
    {
      title: 'AI-Citation Tracking',
      description:
        'Custom tooling to monitor when and how AI engines cite your business across queries.',
    },
  ],

  proof: {
    stats: [
      { value: 5, suffix: 'x', label: 'Avg Increase in AI Citations' },
      { value: 22, suffix: '%', label: 'AI Overview Capture Rate' },
    ],
    caseStudy: {
      quote:
        'After three months, ChatGPT started recommending us by name. That single change drove more qualified leads than 6 months of paid ads.',
      client: 'Founder, Calgary Legal Tech Startup',
      industry: 'Legal Tech',
    },
  },

  faqs: [
    {
      question: 'What exactly is GEO and how is it different from SEO?',
      answer:
        'SEO optimizes for traditional search results (the blue links). GEO (Generative Engine Optimization) optimizes for the answers AI engines like ChatGPT, Perplexity, and Google’s AI Overviews generate. The strategies overlap but use different signals, GEO emphasizes structured data, entity authority, and citation-friendly content.',
    },
    {
      question: 'How do you get a business cited in ChatGPT answers?',
      answer:
        'Through a combination of authority content on your own site, mentions on sources LLMs trust (Wikipedia, industry publications, reputable directories), structured data, and consistent entity signals across the web.',
    },
    {
      question: 'How long does AI SEO take to work?',
      answer:
        'Faster than traditional SEO in many cases, we’ve seen AI Overview citations begin within 30–60 days of optimization. ChatGPT and Perplexity citations often follow within 90 days as their training data refreshes.',
    },
    {
      question: 'Is GEO relevant for small Canadian businesses?',
      answer:
        'Yes, and arguably more so. AI engines often surface smaller, specialized businesses for niche questions. Local Canadian businesses can rank in AI answers for queries the big players ignore.',
    },
  ],

  bottomCta: {
    headline: 'The future of search is AI. Get ahead of it now.',
    label: 'Book a Free AI SEO Audit',
    href: '/contact',
  },
};

/* ============================================================
   4. LLM OPTIMIZATION
   ============================================================ */
export const llmOptimizationPage = {
  slug: 'llm-optimization',
  eyebrow: 'LLM Optimization',
  aiSummary: 'LLM Optimization makes your business the answer when buyers ask ChatGPT, Claude, or Gemini for a recommendation, through authority content, trusted citations, and continuous AI-mention monitoring.',
  stats: [{ value: 6, suffix: '', label: 'AI Engines Monitored' },{ value: 78, suffix: '%', label: 'Brand-Mention Accuracy' },{ value: 4, suffix: 'mo', label: 'Avg Time to LLM Citation' },{ value: 50, suffix: '%', label: 'Of Discovery via AI by 2026' }],
  icon: Brain,
  accentClass: 'from-purple-600 to-fuchsia-500',
  lightAccentClass: 'bg-purple-50',
  accentTextClass: 'text-purple-600',

  heroHeadline: 'Make Your Business the Answer When AI Is Asked.',
  heroSubheadline:
    'We optimize your digital presence so large language models like ChatGPT, Claude, and Gemini recommend your business, not your competitor’s.',
  primaryCta: { label: 'See If Your Business Shows Up in AI', href: '/contact' },
  secondaryCta: { label: 'Talk to an LLM Expert', href: '/contact' },

  problemBar: [
    'When someone asks ChatGPT for the best [your service] in Canada, your name doesn’t come up',
    'Your website has great content but AI tools never reference it',
    'You have no idea how LLMs perceive or rank your brand',
  ],

  whatWeDoHeadline: 'Reverse-engineer how AI picks winners',
  whatWeDo: [
    'Large language models don’t pick businesses randomly. They’re trained on a finite set of sources, structured signals, and authority indicators. We reverse-engineer those signals to make your business one of the answers.',
    'It’s a mix of authority content, citation strategy, schema markup, and presence on the high-trust sources LLMs draw from. The end result: when a buyer asks ChatGPT or Gemini for a recommendation, your business is the answer.',
  ],
  whatWeDoCallout:
    'By 2026, an estimated 50% of discovery searches will happen through AI interfaces. LLM Optimization is no longer optional.',

  servicesBreakdownTitle: 'LLM Optimization Services',
  servicesBreakdown: [
    {
      icon: Search,
      title: 'LLM Brand Audit',
      description:
        'Find out exactly how ChatGPT, Gemini, and Perplexity describe your business today.',
    },
    {
      icon: FileText,
      title: 'Authority Content Buildout',
      description:
        'Create the in-depth, structured content LLMs pull from and cite as authoritative sources.',
    },
    {
      icon: Globe,
      title: 'Citation & Mention Strategy',
      description:
        'Get your business mentioned on the high-trust sources LLMs use for training and retrieval.',
    },
    {
      icon: ChartLine,
      title: 'Ongoing LLM Monitoring',
      description:
        'Track how AI tools describe your brand monthly and adjust strategy in real time.',
    },
  ],

  howItWorks: [
    {
      title: 'LLM Visibility Audit',
      description:
        'What does AI say about you right now? We document baseline answers across major LLMs.',
    },
    {
      title: 'Gap & Competitor Analysis',
      description:
        'Identify which competitors LLMs already mention, and why, so we can close the gap.',
    },
    {
      title: 'Content & Citation Campaign',
      description:
        'Coordinated push of authority content, citations, and structured data over 90–120 days.',
    },
    {
      title: 'Monthly Monitoring & Reporting',
      description:
        'Continuous tracking of AI mentions with quarterly strategy adjustments.',
    },
  ],

  whyBRT: [
    {
      title: 'LLM-Specific Tooling',
      description:
        'We’ve built custom prompts and tracking to monitor brand mentions across 6+ AI engines.',
    },
    {
      title: 'Source-First Strategy',
      description:
        'We focus on the sources LLMs trust, not gimmicks that get filtered out within months.',
    },
    {
      title: 'Quarterly Roadmaps',
      description:
        'AI changes fast. We re-strategize every quarter based on real-world LLM behaviour shifts.',
    },
  ],

  proof: {
    stats: [
      { value: 6, suffix: '', label: 'AI Engines We Monitor' },
      { value: 89, suffix: '%', label: 'Brand-Mention Accuracy Rate' },
    ],
    caseStudy: {
      quote:
        'ChatGPT now describes us using our exact positioning language. That alone has shortened our sales cycle by weeks.',
      client: 'CEO, Montreal Fintech',
      industry: 'Financial Services',
    },
  },

  faqs: [
    {
      question: 'Is LLM Optimization the same as SEO?',
      answer:
        'No. SEO targets ranking on search engine results pages. LLM Optimization targets being cited or recommended inside AI-generated answers. They share some tactics (authority content, structured data) but require different strategies.',
    },
    {
      question: 'Which AI tools do you optimize for?',
      answer:
        'ChatGPT, Claude, Gemini, Perplexity, Microsoft Copilot, and Google AI Overviews. We track all six monthly and adjust strategy as their training data and retrieval methods evolve.',
    },
    {
      question: 'How do you measure success in LLM Optimization?',
      answer:
        'Three core metrics: (1) presence, does the LLM mention your brand at all? (2) accuracy, does it describe you correctly? (3) recommendation, does it position you as a top option? We track all three monthly.',
    },
    {
      question: 'How soon can we expect to appear in AI answers?',
      answer:
        'Initial mentions often appear within 60–90 days as LLMs refresh their data. Consistent, prominent citations typically take 4–6 months of sustained authority-building.',
    },
  ],

  bottomCta: {
    headline: 'If AI doesn’t know you, your next customer won’t either.',
    label: 'Book a Free LLM Audit',
    href: '/contact',
  },
};

/* ============================================================
   5. ECOMMERCE SEO
   ============================================================ */
export const ecommerceSeoPage = {
  slug: 'ecommerce',
  eyebrow: 'Ecommerce SEO',
  aiSummary: "Blue Ribbon's Ecommerce SEO gets Canadian online stores ranking on Google, Amazon, and Google Shopping through product-page optimization, technical SEO, and catalogue-scale content strategy.",
  stats: [{ value: 145, suffix: '%', label: 'Organic Revenue Growth' },{ value: 32, suffix: '%', label: 'Cost-Per-Acquisition Cut' },{ value: 5, suffix: '+', label: 'Platforms Supported' },{ value: 3, suffix: 'mo', label: 'Typical Payback' }],
  icon: ShoppingCart,
  accentClass: 'from-orange-600 to-amber-500',
  lightAccentClass: 'bg-orange-50',
  accentTextClass: 'text-orange-600',

  heroHeadline: 'Turn Your Product Pages Into a 24/7 Sales Machine.',
  heroSubheadline:
    'Ecommerce SEO that gets your products ranking, your cart filling, and your revenue growing, without paying for every click.',
  primaryCta: { label: 'Get a Free Ecommerce SEO Audit', href: '/contact' },
  secondaryCta: { label: 'See How It Works', href: '#services-grid' },

  problemBar: [
    'Your Shopify store gets traffic but visitors don’t buy',
    'Your product pages don’t rank because they’re just manufacturer descriptions',
    'You’re spending thousands on ads because organic just isn’t working',
  ],

  whatWeDoHeadline: 'SEO built for product catalogues, not blog posts',
  whatWeDo: [
    'Ecommerce SEO is its own discipline. Faceted navigation, product variants, structured data, marketplace integrations, these don’t exist in standard SEO playbooks.',
    'We specialize in turning product catalogues into traffic engines. From category page optimization to marketplace SEO on Amazon and Google Shopping, we cover every angle of how shoppers find products in Canada.',
  ],

  servicesBreakdownTitle: 'Ecommerce SEO Services',
  servicesBreakdown: [
    {
      icon: ShoppingCart,
      title: 'Product Page Optimization',
      description:
        'Titles, descriptions, schema markup, and internal linking that ranks and converts.',
    },
    {
      icon: Globe,
      title: 'Category Page SEO',
      description:
        'Turn collection pages into high-traffic landing pages Google loves to rank.',
    },
    {
      icon: PenTool,
      title: 'Ecommerce Content Strategy',
      description:
        'Buying guides, comparisons, and blog posts that capture shoppers at every stage.',
    },
    {
      icon: Wrench,
      title: 'Technical Ecommerce SEO',
      description:
        'Fix crawl issues, site speed, duplicate content, and faceted navigation problems.',
    },
    {
      icon: TrendingUp,
      title: 'Amazon & Marketplace SEO',
      description:
        'Rank beyond your website, on Amazon, Google Shopping, and Canadian marketplaces.',
    },
  ],

  platformBadges: ['Shopify', 'WooCommerce', 'Magento', 'BigCommerce', 'Custom'],

  howItWorks: [
    {
      title: 'Ecommerce SEO Audit',
      description:
        'A 100+ point audit covering product pages, categories, technical health, and structure.',
    },
    {
      title: 'Keyword & Competitor Research',
      description:
        'Map every product to the keywords your buyers use, including long-tail and intent-based queries.',
    },
    {
      title: 'On-Page & Technical Fixes',
      description:
        'Roll out optimizations across the catalogue. Fix crawl, duplicate content, and schema issues.',
    },
    {
      title: 'Content Buildout & Link Earning',
      description:
        'Buying guides, gift guides, and comparison content that earns links and ranks for high-intent terms.',
    },
  ],

  whyBRT: [
    {
      title: 'Catalogue-Scale Optimization',
      description:
        'Whether you have 50 SKUs or 50,000, we have systems to scale optimization without sacrificing quality.',
    },
    {
      title: 'Conversion-First Mindset',
      description:
        'Rankings without conversions are worthless. We optimize the full path from search to checkout.',
    },
    {
      title: 'Canadian Marketplace Expertise',
      description:
        'Deep experience with Canadian-specific platforms, shipping, taxes, and bilingual storefronts.',
    },
  ],

  proof: {
    stats: [
      { value: 280, suffix: '%', label: 'Avg Organic Revenue Growth (Year 1)' },
      { value: 64, suffix: '%', label: 'Reduction in Cost-Per-Acquisition' },
    ],
    caseStudy: {
      quote:
        'Within 9 months our organic traffic doubled and we cut paid ad spend by 40%. The ROI was clear from month three.',
      client: 'Founder, Ottawa DTC Brand',
      industry: 'Ecommerce, Outdoor Gear',
    },
  },

  faqs: [
    {
      question: 'How is ecommerce SEO different from regular SEO?',
      answer:
        'Ecommerce SEO has unique challenges: faceted navigation creates massive duplicate content, product pages often share content with other retailers, structured data is critical for rich results, and inventory changes constantly. We use specialized tools and processes built for these challenges.',
    },
    {
      question: 'Can you work with our existing Shopify theme?',
      answer:
        'Yes, in 90% of cases we work within your current theme. If structural changes are needed, we provide developer-ready specs for your team or implement them ourselves.',
    },
    {
      question: 'How do you handle seasonal product pages?',
      answer:
        'Strategic. We build evergreen URL structures, redirect retired products properly, and optimize seasonal pages months ahead so they rank when traffic peaks.',
    },
    {
      question: 'Do you optimize for Canadian shipping and local search?',
      answer:
        'Absolutely. We handle hreflang, currency, local schema, regional shipping mentions, and tax/duty considerations, critical for ranking against US competitors in Canadian results.',
    },
  ],

  bottomCta: {
    headline: 'Your products deserve to be found. Let’s make it happen.',
    label: 'Book a Free Ecommerce SEO Audit',
    href: '/contact',
  },
};

/* ============================================================
   6. TECHNICAL SEO
   ============================================================ */
export const technicalSeoPage = {
  slug: 'technical',
  eyebrow: 'Technical SEO',
  aiSummary: "Blue Ribbon's Technical SEO audits and fixes 100+ site-health issues, from Core Web Vitals to schema markup, so Google and AI engines can crawl, index, and rank your site with confidence.",
  stats: [{ value: 60, suffix: '%', label: 'Core Web Vitals Improvement' },{ value: 65, suffix: '%', label: 'Indexed Pages Increase' },{ value: 100, suffix: '+', label: 'Audit Checkpoints' },{ value: 3, suffix: ' wks', label: 'Avg Audit Delivery' }],
  icon: Wrench,
  accentClass: 'from-slate-700 to-slate-500',
  lightAccentClass: 'bg-slate-100',
  accentTextClass: 'text-slate-700',

  heroHeadline: 'Fix What’s Holding Your Website Back From Ranking.',
  heroSubheadline:
    'Great content can’t rank on a broken foundation. Our Technical SEO service finds and fixes every issue stopping Google from crawling, indexing, and ranking your site.',
  primaryCta: { label: 'Get a Free Technical SEO Audit', href: '/contact' },
  secondaryCta: { label: 'Talk to a Technical SEO Expert', href: '/contact' },

  problemBar: [
    'Your site is slow and you’re losing rankings because of it',
    'Google is crawling your site but not indexing your best pages',
    'You’ve done everything right with content but rankings won’t move',
  ],

  whatWeDoHeadline: 'The foundation every other SEO effort sits on',
  whatWeDo: [
    'You can write the best content in your industry, but if Google can’t crawl it, render it, or trust it, none of that matters. Technical SEO is the foundation that makes every other marketing effort work harder.',
    'Our 100+ point technical audit covers every layer: server response, rendering, indexing, schema, internal linking, and Core Web Vitals. We don’t hand you a PDF and walk away, we fix what’s broken or work directly with your dev team.',
  ],

  servicesBreakdownTitle: 'Technical SEO Services',
  servicesBreakdown: [
    {
      icon: Zap,
      title: 'Site Speed & Core Web Vitals',
      description:
        'Optimize LCP, FID, and CLS to meet Google’s performance standards.',
    },
    {
      icon: Search,
      title: 'Crawl & Index Audit',
      description:
        'Find and fix crawl blocks, noindex errors, and orphaned pages.',
    },
    {
      icon: Globe,
      title: 'Site Architecture & Internal Linking',
      description:
        'Build a logical URL structure and link hierarchy Google can follow.',
    },
    {
      icon: FileText,
      title: 'Schema Markup & Structured Data',
      description:
        'Add rich snippet markup to win featured snippets and AI Overviews.',
    },
    {
      icon: Wrench,
      title: 'Duplicate Content & Canonicalization',
      description:
        'Eliminate content duplication that splits your ranking authority.',
    },
    {
      icon: ChartLine,
      title: 'Mobile & Accessibility Optimization',
      description:
        'Pass Google’s mobile-first indexing and accessibility requirements.',
    },
  ],

  howItWorks: [
    {
      title: 'Full Technical Audit',
      description:
        'A 100+ point checklist using Screaming Frog, Sitebulb, GSC, and custom crawlers.',
    },
    {
      title: 'Priority Issues Report',
      description:
        'Issues ranked by impact and effort. You see exactly what to fix first and why.',
    },
    {
      title: 'Implementation',
      description:
        'We fix it ourselves, or guide your dev team with detailed specs and PR reviews.',
    },
    {
      title: 'Recheck & Ongoing Monitoring',
      description:
        'Quarterly recrawls with alerts for any new issues that creep in over time.',
    },
  ],

  whyBRT: [
    {
      title: 'Engineering-Backed Audits',
      description:
        'Our auditors come from a development background, they speak your dev team’s language.',
    },
    {
      title: 'Hands-On Implementation',
      description:
        'We don’t just find problems. We can fix them, across most modern stacks and CMSs.',
    },
    {
      title: 'Transparent Specs',
      description:
        'Every recommendation comes with code-level detail your team can review and ship.',
    },
  ],

  proof: {
    stats: [
      { value: 87, suffix: '%', label: 'Avg Core Web Vitals Improvement' },
      { value: 134, suffix: '%', label: 'Avg Indexed Pages Increase' },
    ],
    caseStudy: {
      quote:
        'Their audit caught a redirect chain we’d had for 3 years. Fixing it alone recovered 40% of our lost organic traffic.',
      client: 'Head of Digital, Edmonton Retailer',
      industry: 'Ecommerce, Home Goods',
    },
  },

  faqs: [
    {
      question: 'How long does a technical SEO audit take?',
      answer:
        'Most audits take 2–3 weeks from kickoff to delivery. We move faster for smaller sites and need more time for sites with 100k+ pages or complex tech stacks.',
    },
    {
      question: 'Do you work with our existing development team?',
      answer:
        'Yes, that’s our preferred model. We provide detailed specs, code review, and Slack support so your devs can ship fixes confidently.',
    },
    {
      question: 'Will you fix the issues or just tell us what they are?',
      answer:
        'Both options available. We can hand off a prioritized roadmap, implement fixes ourselves, or co-implement alongside your team. Most clients choose a hybrid.',
    },
    {
      question: 'How often should we do a technical SEO audit?',
      answer:
        'A full audit annually, plus quarterly mini-audits. Sites that publish or change frequently benefit from monthly crawl monitoring.',
    },
  ],

  bottomCta: {
    headline:
      'A fast, clean, crawlable site is the foundation of every successful SEO strategy.',
    label: 'Book a Free Technical Audit',
    href: '/contact',
  },
};

/* ============================================================
   7. SEO COPYWRITING
   ============================================================ */
export const seoCopywritingPage = {
  slug: 'copywriting',
  eyebrow: 'SEO Copywriting',
  aiSummary: 'Blue Ribbon writes bilingual SEO content for Canadian businesses that Google rewards and real people actually want to read: blogs, landing pages, product copy, and thought leadership.',
  stats: [{ value: 120, suffix: '%', label: 'Avg Blog Traffic Growth' },{ value: 22, suffix: '%', label: 'Conversion Lift on Rewrites' },{ value: 2, suffix: '', label: 'Languages Delivered (EN + FR)' },{ value: 7, suffix: ' days', label: 'Avg Turnaround per Piece' }],
  icon: PenTool,
  accentClass: 'from-emerald-600 to-teal-500',
  lightAccentClass: 'bg-emerald-50',
  accentTextClass: 'text-emerald-600',

  heroHeadline: 'Words That Rank. Copy That Converts. Content That Does Both.',
  heroSubheadline:
    'We write SEO content for Canadian businesses that Google rewards and real people actually want to read, not keyword-stuffed filler.',
  primaryCta: { label: 'Get a Free Content Audit', href: '/contact' },
  secondaryCta: { label: 'See Writing Samples', href: '/case-studies' },

  problemBar: [
    'Your blog posts get zero traffic because they’re not optimized to rank',
    'Your web copy explains what you do but doesn’t make anyone take action',
    'You’ve tried AI content tools but the output sounds generic and off-brand',
  ],

  whatWeDoHeadline: 'Sharper, on-brand, ready to rank',
  whatWeDo: [
    'Most content is forgettable. We write the kind of content that actually gets read, shared, and ranked. Every piece is researched, structured for SEO, and matched to your brand voice.',
    'We don’t use AI to replace writers, we use it to help our human team work smarter. Every word that ships is reviewed, edited, and polished by an experienced human editor.',
  ],
  whatWeDoCallout:
    'Every piece we write is matched to your brand voice. We interview your team, study your market, and write like you, only sharper.',

  servicesBreakdownTitle: 'SEO Copywriting Services',
  servicesBreakdown: [
    {
      icon: FileText,
      title: 'SEO Blog & Article Writing',
      description:
        'Research-backed long-form content built around the keywords your buyers actually search.',
    },
    {
      icon: Globe,
      title: 'Website Copywriting',
      description:
        'Homepage, service pages, and about pages written to rank and convert.',
    },
    {
      icon: Target,
      title: 'Landing Page Copy',
      description:
        'High-converting copy for PPC and campaign landing pages.',
    },
    {
      icon: ShoppingCart,
      title: 'Product & Category Descriptions',
      description:
        'Ecommerce copy that’s unique, scannable, and optimized for shoppers and search.',
    },
    {
      icon: Linkedin,
      title: 'Thought Leadership & LinkedIn Content',
      description:
        'Position your founders and leadership as Canadian industry experts.',
    },
  ],

  howItWorks: [
    {
      title: 'Keyword & Intent Research',
      description:
        'We map keywords to buyer intent so each piece targets a real, profitable query.',
    },
    {
      title: 'Content Brief & Outline Approval',
      description:
        'Before any writing happens, you approve the angle, structure, and key points.',
    },
    {
      title: 'Writing, Editing & SEO Optimization',
      description:
        'A human writer drafts, an editor polishes, and an SEO specialist optimizes.',
    },
    {
      title: 'Publish, Track & Refresh',
      description:
        'We publish, track rankings, and refresh content quarterly to maintain rank.',
    },
  ],

  whyBRT: [
    {
      title: 'Bilingual Writers',
      description:
        'Native English and French writers, fully fluent for both Canadian markets.',
    },
    {
      title: 'Voice Matching',
      description:
        'We interview your team and study your market to nail your brand voice in every piece.',
    },
    {
      title: 'Human-Edited',
      description:
        'AI assists. Humans decide. Every piece is reviewed, edited, and polished before ship.',
    },
  ],

  proof: {
    stats: [
      { value: 120, suffix: '%', label: 'Avg Blog Traffic Growth' },
      { value: 38, suffix: '%', label: 'Avg Conversion Rate Lift on Rewritten Pages' },
    ],
    caseStudy: {
      quote:
        'Their team gets our voice better than our internal team did. The content reads like us, only sharper and more strategic.',
      client: 'VP Marketing, Toronto B2B SaaS',
      industry: 'B2B SaaS',
    },
  },

  faqs: [
    {
      question: 'Do you write in both English and French?',
      answer:
        'Yes, fully bilingual. We have native French-speaking writers and editors. We don’t translate; we write fresh in each language so the copy feels native, not converted.',
    },
    {
      question: 'Can you match our existing brand voice?',
      answer:
        'Absolutely. We start every engagement with a voice discovery, interviews with your team, samples of your existing best content, and a custom voice guide we use across all writers.',
    },
    {
      question: 'How do you handle topic research?',
      answer:
        'Every piece starts with keyword research, competitor analysis, and SME (subject matter expert) interviews when needed. The result: content that’s deeper and more useful than what’s currently ranking.',
    },
    {
      question: 'What’s your turnaround time per piece?',
      answer:
        'Standard blog post: 7–10 business days from brief approval. Long-form pillar content: 2–3 weeks. Rush options available for time-sensitive campaigns.',
    },
  ],

  bottomCta: {
    headline: 'Great content is your best salesperson. Let’s write it.',
    label: 'Book a Free Content Strategy Call',
    href: '/contact',
  },
};

/* ============================================================
   8. SOCIAL MEDIA MANAGEMENT
   ============================================================ */
export const socialMediaPage = {
  slug: 'social-media',
  eyebrow: 'Social Media Management',
  aiSummary: "Blue Ribbon's Social Media Management is fully done-for-you: strategy, content, scheduling, engagement, and reporting across LinkedIn, Instagram, Facebook, X, and TikTok, tuned for Canadian SMBs.",
  stats: [{ value: 140, suffix: '%', label: 'Avg LinkedIn Follower Growth' },{ value: 2.4, suffix: 'x', label: 'Engagement Rate vs Industry' },{ value: 5, suffix: '', label: 'Platforms Managed' },{ value: 12, suffix: '+', label: 'Posts per Month' }],
  icon: Share2,
  accentClass: 'from-pink-600 to-rose-500',
  lightAccentClass: 'bg-pink-50',
  accentTextClass: 'text-pink-600',

  heroHeadline: 'Show Up Consistently. Build an Audience That Actually Buys.',
  heroSubheadline:
    'Done-for-you social media management for Canadian businesses that want to grow their brand, without spending all day on Instagram.',
  primaryCta: { label: 'Get a Free Social Media Audit', href: '/contact' },
  secondaryCta: { label: 'See Our Packages', href: '#services-grid' },

  problemBar: [
    'You post occasionally but get no engagement and zero leads from it',
    'You know you should be on LinkedIn but never have time to show up',
    'Your social presence looks inconsistent and doesn’t reflect how good your business actually is',
  ],

  whatWeDoHeadline: 'Showing up is half the battle. We handle the rest.',
  whatWeDo: [
    'Most businesses know they should be active on social media. The problem isn’t strategy, it’s execution. Daily content creation, posting, engagement, and reporting eats hours your team doesn’t have.',
    'We become your social media team. Strategy, content creation, scheduling, community management, and reporting, all handled. You stay focused on running the business.',
  ],

  servicesBreakdownTitle: 'Social Media Services',
  servicesBreakdown: [
    {
      icon: PenTool,
      title: 'Content Creation & Scheduling',
      description:
        'Monthly content calendars, graphics, captions, and posting, fully handled.',
    },
    {
      icon: Linkedin,
      title: 'LinkedIn Management (B2B)',
      description:
        'Build your company and founder presence on the #1 B2B platform in Canada.',
    },
    {
      icon: Instagram,
      title: 'Instagram & Facebook Management',
      description:
        'Consistent, on-brand content that builds community and drives traffic.',
    },
    {
      icon: MessageSquare,
      title: 'Community Management',
      description:
        'We respond to comments, DMs, and mentions so your audience feels heard.',
    },
    {
      icon: BarChart3,
      title: 'Social Media Reporting',
      description:
        'Monthly reports showing what’s growing, what’s converting, and what we’re adjusting.',
    },
  ],

  platformBadges: ['LinkedIn', 'Instagram', 'Facebook', 'X (Twitter)', 'TikTok'],

  howItWorks: [
    {
      title: 'Social Audit & Strategy Session',
      description:
        'Audit current channels, identify gaps, define platform priorities and KPIs.',
    },
    {
      title: 'Brand Voice & Content Style Guide',
      description:
        'Document your voice, visuals, and content pillars so every post feels consistent.',
    },
    {
      title: 'Monthly Content Creation & Scheduling',
      description:
        'A full calendar of posts: graphics, captions, hashtags. You approve, we publish.',
    },
    {
      title: 'Engage, Report & Optimize',
      description:
        'We respond to engagement, track performance, and refine each month.',
    },
  ],

  whyBRT: [
    {
      title: 'Strategy + Execution',
      description:
        'Most agencies offer one or the other. We do both, and tie social to real business outcomes.',
    },
    {
      title: 'Founder-Led LinkedIn',
      description:
        'We can manage your CEO’s personal LinkedIn, often the highest-ROI social channel for B2B.',
    },
    {
      title: 'Crisis-Ready',
      description:
        'Trained in PR and crisis response. We escalate sensitive comments to you immediately.',
    },
  ],

  proof: {
    stats: [
      { value: 285, suffix: '%', label: 'Avg LinkedIn Follower Growth (Year 1)' },
      { value: 4.6, suffix: 'x', label: 'Avg Engagement Rate vs Industry' },
    ],
    caseStudy: {
      quote:
        'Our LinkedIn went from 1,200 followers to 8,400 in a year, and three of our biggest deals came directly from inbound DMs.',
      client: 'CEO, Toronto Consulting Firm',
      industry: 'Professional Services',
    },
  },

  faqs: [
    {
      question: 'How many posts per week do we get?',
      answer:
        'Standard packages include 3–5 posts per week per platform. We tailor frequency based on platform best practices, your audience, and your goals.',
    },
    {
      question: 'Do you create the graphics or do we need a designer?',
      answer:
        'We create everything. Branded templates, custom graphics, video edits, and carousels, all in-house. You don’t need an internal designer.',
    },
    {
      question: 'Can you manage our CEO’s personal LinkedIn too?',
      answer:
        'Yes, and we recommend it for B2B clients. Founder-led content typically gets 3–5x the engagement of corporate accounts on LinkedIn.',
    },
    {
      question: 'How do you handle negative comments or a PR issue?',
      answer:
        'We have an escalation protocol. Standard negative comments are handled per your brand guidelines. Anything sensitive is flagged to you within 1 hour with a recommended response.',
    },
  ],

  bottomCta: {
    headline: 'Your audience is on social. Let’s make sure they’re seeing you.',
    label: 'Book a Free Social Media Audit',
    href: '/contact',
  },
};

/* ============================================================
   9. PPC & PAID ADVERTISING
   ============================================================ */
export const ppcPage = {
  slug: 'ppc',
  eyebrow: 'PPC & Paid Advertising',
  aiSummary: 'Blue Ribbon runs Google, Meta, and LinkedIn paid ads on a flat monthly fee (no percentage of ad spend), optimizing for qualified leads and revenue, not vanity clicks.',
  stats: [{ value: 25, suffix: '%', label: 'Avg Cost-Per-Lead Reduction' },{ value: 3.4, suffix: 'x', label: 'Return on Ad Spend (ROAS)' },{ value: 90, suffix: ' days', label: 'To Predictable Performance' },{ value: 0, suffix: '%', label: 'Percent of Ad Spend Charged' }],
  icon: Target,
  accentClass: 'from-amber-600 to-orange-500',
  lightAccentClass: 'bg-amber-50',
  accentTextClass: 'text-amber-600',

  heroHeadline: 'Stop Burning Ad Budget. Start Getting Qualified Leads.',
  heroSubheadline:
    'Data-driven paid advertising for Canadian businesses that want more leads, lower cost-per-click, and campaigns they can actually see working.',
  primaryCta: { label: 'Get a Free Ad Account Audit', href: '/contact' },
  secondaryCta: { label: 'See How We Run Ads', href: '#services-grid' },

  problemBar: [
    'You’re spending $3,000/month on Google Ads and can’t tell if it’s working',
    'Your cost-per-lead keeps climbing and your agency can’t explain why',
    'You tried running ads yourself and burned through the budget with nothing to show',
  ],

  whatWeDoHeadline: 'Ads tied to revenue. Always.',
  whatWeDo: [
    "Bad paid advertising looks expensive because it is. Most agencies optimize for clicks and impressions, metrics that don't pay your bills. We optimize for qualified leads, sales, and revenue.",
    'Every campaign we run is tied to a real conversion event in your business. Every dollar you spend is tracked from click to customer. No black-box reporting. No vague “engagement” metrics. Just qualified leads at a sustainable cost.',
  ],

  servicesBreakdownTitle: 'Paid Advertising Services',
  servicesBreakdown: [
    {
      icon: Search,
      title: 'Google Search Ads',
      description:
        'Capture high-intent buyers the moment they search for what you offer.',
    },
    {
      icon: Facebook,
      title: 'Meta Ads (Facebook & Instagram)',
      description:
        'Reach and retarget your ideal customer across the Meta ecosystem.',
    },
    {
      icon: Linkedin,
      title: 'LinkedIn Ads',
      description:
        'The most powerful B2B ad platform in Canada, we run it right.',
    },
    {
      icon: TrendingUp,
      title: 'Retargeting Campaigns',
      description:
        'Win back visitors who didn’t convert the first time with precision retargeting.',
    },
    {
      icon: Globe,
      title: 'Landing Page Optimization',
      description:
        'Ads only work if the page converts, we optimize both together.',
    },
  ],

  howItWorks: [
    {
      title: 'Ad Account Audit & Competitor Research',
      description:
        'Deep audit of current spend, structure, and competitor positioning before any change.',
    },
    {
      title: 'Campaign Strategy & Budget Planning',
      description:
        'Channel mix, audience targeting, budget allocation, and KPI definition.',
    },
    {
      title: 'Ad Creative, Copywriting & Launch',
      description:
        'New creative, conversion-focused copy, and tightly-built campaigns ready to scale.',
    },
    {
      title: 'Weekly Optimization & Monthly Reporting',
      description:
        'Daily monitoring, weekly optimization, monthly strategic reporting calls.',
    },
  ],

  whyBRT: [
    {
      title: 'Flat Fee, Not % of Spend',
      description:
        'We charge a flat monthly fee. Most agencies take 10–20% of ad spend, that creates the wrong incentive.',
    },
    {
      title: 'Full Transparency',
      description:
        'You own all accounts. You see every dollar. Live dashboards. No black-box reporting.',
    },
    {
      title: 'Lead-Quality Focus',
      description:
        'We optimize for qualified leads, not raw conversions. Quality over quantity, every time.',
    },
  ],

  proof: {
    stats: [
      { value: 40, suffix: '%', label: 'Avg Cost-Per-Lead Reduction in 90 Days' },
      { value: 4.2, suffix: 'x', label: 'Avg Return on Ad Spend (ROAS)' },
    ],
    caseStudy: {
      quote:
        'In 90 days they cut our cost-per-lead in half and doubled our pipeline. We finally have a paid channel we can scale with confidence.',
      client: 'Head of Sales, Calgary Energy SaaS',
      industry: 'B2B SaaS, Energy Sector',
    },
  },

  faqs: [
    {
      question: 'What’s the minimum ad budget you work with?',
      answer:
        'For most channels, $3,000/month in ad spend is the practical minimum to gather meaningful data. LinkedIn typically requires $5,000+/month due to higher CPCs.',
    },
    {
      question: 'Do you charge a percentage of ad spend or a flat fee?',
      answer:
        'Always a flat monthly fee. Percentage-based pricing creates the wrong incentive, we want to scale spend only when it’s profitable for you, not when it’s profitable for us.',
    },
    {
      question: 'How long before we see results from paid ads?',
      answer:
        'Initial conversions usually appear within 2–4 weeks. Optimized, predictable performance typically takes 60–90 days as we collect data and refine targeting.',
    },
    {
      question: 'Can you audit our existing Google Ads account?',
      answer:
        'Yes, and we offer a free deep-dive audit for any account spending $3k+/month. We’ll show you exactly what’s working, what’s wasted, and what to fix.',
    },
  ],

  bottomCta: {
    headline: 'Every day you run bad ads is money you won’t get back. Let’s fix it.',
    label: 'Book a Free Ad Account Audit',
    href: '/contact',
  },
};

/* ============================================================
   10. BRAND STRATEGY & ANALYTICS
   ============================================================ */
export const brandStrategyPage = {
  slug: 'brand-strategy',
  eyebrow: 'Brand Strategy & Analytics',
  aiSummary: 'Blue Ribbon combines brand positioning with unified analytics dashboards so Canadian companies look the part, prove marketing works, and make decisions from data, not gut feel.',
  stats: [{ value: 22, suffix: '%', label: 'Avg Conversion Rate Lift' },{ value: 40, suffix: '%', label: 'Time Saved on Reporting' },{ value: 6, suffix: '+', label: 'Analytics Tools Integrated' },{ value: 1, suffix: '', label: 'Single Live Dashboard' }],
  icon: Palette,
  accentClass: 'from-violet-600 to-purple-500',
  lightAccentClass: 'bg-violet-50',
  accentTextClass: 'text-violet-600',

  heroHeadline: 'Build a Brand Canadian Buyers Trust, Then Measure Everything.',
  heroSubheadline:
    'From brand positioning to performance dashboards, we help growing Canadian companies look the part and prove their marketing is working.',
  primaryCta: { label: 'Book a Free Brand Strategy Call', href: '/contact' },
  secondaryCta: { label: 'See Our Analytics Approach', href: '#services-grid' },

  problemBar: [
    'You have a logo but not a brand, and you feel it every time you pitch a client',
    'Your marketing data lives in 5 different tools and no one knows what’s actually driving growth',
    'You’re making marketing decisions based on gut feel, not numbers',
  ],

  whatWeDoHeadline: 'Brand + Data = Defensible Growth',
  whatWeDo: [
    'A clear brand makes every marketing dollar work harder. Defined positioning, sharp messaging, and consistent visual identity create trust before a single sales conversation.',
    "But brand without data is guesswork. We pair brand strategy with unified analytics, one dashboard showing every channel, every conversion, every dollar of revenue. So you know what's working, what's not, and where to invest next.",
  ],

  servicesBreakdownTitle: 'Brand & Analytics Services',
  servicesBreakdown: [
    {
      icon: Lightbulb,
      title: 'Brand Positioning & Messaging',
      description:
        'Define who you are, who you serve, and why you’re the only logical choice.',
    },
    {
      icon: Palette,
      title: 'Visual Identity & Brand Guidelines',
      description:
        'Logos, colors, typography, and usage rules your whole team can follow.',
    },
    {
      icon: BarChart3,
      title: 'Unified Analytics Dashboard',
      description:
        'One live dashboard showing all your marketing channels, leads, and revenue.',
    },
    {
      icon: LineChart,
      title: 'Conversion Rate Optimization (CRO)',
      description:
        'Data and testing to turn more of your existing traffic into customers.',
    },
    {
      icon: FileText,
      title: 'Monthly Marketing Reporting',
      description:
        'Plain-English reports showing what’s working, what’s not, and what to do next.',
    },
  ],

  platformBadges: [
    'Google Analytics',
    'GA4',
    'Looker Studio',
    'HubSpot',
    'Semrush',
    'Meta Ads Manager',
  ],

  howItWorks: [
    {
      title: 'Brand & Analytics Audit',
      description:
        'Audit current brand assets, messaging, and the analytics stack you’re using today.',
    },
    {
      title: 'Positioning Workshop & Dashboard Setup',
      description:
        'Hands-on workshop to nail positioning. Build the unified analytics dashboard.',
    },
    {
      title: 'Brand Rollout & Analytics Integration',
      description:
        'Roll out updated brand assets. Integrate every marketing channel into one dashboard.',
    },
    {
      title: 'Monthly Review & Optimization',
      description:
        'Monthly reporting calls. Brand refinements. CRO tests. Continuous improvement.',
    },
  ],

  whyBRT: [
    {
      title: 'Brand-to-Revenue Connection',
      description:
        'We measure brand impact in actual revenue terms, not just awareness or sentiment.',
    },
    {
      title: 'Live Dashboards',
      description:
        'You don’t wait for monthly reports. Live data, refreshed continuously, available 24/7.',
    },
    {
      title: 'Strategy Workshops',
      description:
        'Brand workshops led by senior strategists, not junior account managers.',
    },
  ],

  proof: {
    stats: [
      { value: 22, suffix: '%', label: 'Avg Conversion Rate Lift' },
      { value: 40, suffix: '%', label: 'Time Saved on Reporting' },
    ],
    caseStudy: {
      quote:
        'For the first time we have one number that tells us if marketing is working. It changed every conversation we have at the leadership table.',
      client: 'Founder, Vancouver SaaS Startup',
      industry: 'Technology, Series A',
    },
  },

  faqs: [
    {
      question: 'Do you do full rebrands or just positioning?',
      answer:
        'Both. Some clients need just positioning and messaging refinement. Others need full visual rebrands including logos, color systems, and brand guidelines. We scope to your needs.',
    },
    {
      question: 'What analytics tools do you integrate with?',
      answer:
        'Google Analytics, GA4, Looker Studio, HubSpot, Salesforce, Mixpanel, Semrush, Ahrefs, Meta Ads Manager, LinkedIn Campaign Manager, and most major marketing tools. If it has an API, we can integrate it.',
    },
    {
      question: 'How do you connect brand work to actual revenue?',
      answer:
        'Through a combination of attribution modeling, cohort analysis, and conversion rate measurement. We don’t claim brand directly drives every sale, but we can quantify its lift on every other channel.',
    },
    {
      question: 'Can you work with our existing design team?',
      answer:
        'Absolutely. We often co-create brand systems with internal teams. We bring strategic clarity; your designers bring execution. We document everything so handoffs are seamless.',
    },
  ],

  bottomCta: {
    headline: 'A strong brand makes every other marketing dollar work harder.',
    label: 'Book a Free Brand Strategy Call',
    href: '/contact',
  },
};

/* ============================================================
   EXPORT MAP, for routing convenience
   ============================================================ */
export const marketingPagesMap = {
  hub: digitalMarketingHub,
  seoHub,
  'ai-seo': aiSeoPage,
  'llm-optimization': llmOptimizationPage,
  ecommerce: ecommerceSeoPage,
  technical: technicalSeoPage,
  copywriting: seoCopywritingPage,
  'social-media': socialMediaPage,
  ppc: ppcPage,
  'brand-strategy': brandStrategyPage,
};
